import {
  useCompany,
  useGetCompanyAttributeOptions,
  useGetCWSSummary,
  useGetQualifiedResourcesPost,
  useIngressRequest,
} from '@dispatch/shared/hooks';
import {
  TaskCreate_FormSections,
  FNOL_RequestForm,
  IngressRequestForm,
  IngressRequest,
  FNOL_TaskTypes,
  QualifiedResourcePost,
  QualifiedResourcesFNOLFilters,
} from '@dispatch/shared/types';
import {
  getClaimAttributeDisplayNameLookup,
  isDateOfLossLocked,
  QualifiedResourcesTaskCreate,
} from '@dispatch/web/features/tasks';
import { useNotification } from '@dispatch/web/hooks';
import { zonedTimeToUtc } from 'date-fns-tz';
import { isEmpty, isNull } from 'lodash';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { v4 as uuidv4 } from 'uuid';

import { applyAdditionalDataToIngressRequest } from './applyAdditionalDataToIngressRequest';

const FIELD = 'FIELD';

export function QualifiedResourcesTaskCreatePage() {
  const [formSection, setFormSection] = useState<TaskCreate_FormSections>(
    TaskCreate_FormSections.INITIAL
  );
  const [selectedResource, setSelectedResource] =
    useState<QualifiedResourcePost | null>(null);
  const [qualifiedResources, setQualifiedResources] = useState<
    QualifiedResourcePost[] | undefined
  >([]);
  const [saveButtonLabel, setSaveButtonLabel] = useState('Next');

  const { notifyError, notifySuccess } = useNotification();

  const [claimNumberInput, setClaimNumberInput] = useState<string>('');
  const [claimNumberDebouncedValue, setClaimNumberDebouncedValue] =
    useState<string>('');

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [timeZone, setTimeZone] = useState<string>('');

  useDebounce(
    () => {
      setClaimNumberDebouncedValue(claimNumberInput);
    },
    1000,
    [claimNumberInput]
  );

  const cwsSummary = useGetCWSSummary(
    claimNumberDebouncedValue,
    !!claimNumberDebouncedValue
  );
  const isDolLocked = isDateOfLossLocked(cwsSummary.summary);

  const [qualifiedResourcesFilters, setQualifiedResourcesFilters] =
    React.useState<QualifiedResourcesFNOLFilters>(
      {} as QualifiedResourcesFNOLFilters
    );

  const qualifiedResourcesPost = useGetQualifiedResourcesPost(
    qualifiedResourcesFilters,
    !isEmpty(qualifiedResourcesFilters)
  );

  React.useEffect(() => {
    setQualifiedResources(qualifiedResourcesPost.data);
  }, [qualifiedResourcesPost.data]);

  const fnolRequestHelper = (
    values: FNOL_RequestForm
  ): QualifiedResourcesFNOLFilters => {
    return {
      lossLocation: values.lossLocation,
      methodOfInspection: FIELD,
      catCode: values.catCode,
      severity: values.severity,
      peril: values.peril,
      startTime: values.startTime.toISOString(),
      endTime: values.endTime.toISOString(),
      durationInMinutes: isNull(values.durationInMinutes)
        ? 0
        : values.durationInMinutes,
      preferredLanguage: values.preferredLanguage,
      assignmentCategory: values.assignmentCategory,
      requiredAttributes:
        values.claimAttributes.length > 0
          ? Object.assign(
              {},
              ...values.claimAttributes.map((value) => ({
                [value.attributeDefinitionIntegrationGUID]:
                  value.selectedAttributeValuesVarchars,
              }))
            )
          : undefined,
      resourceIds: values.resourceIds
        ? values.resourceIds.map((value) => {
            return value.value as string;
          })
        : undefined,
    };
  };

  const onHandleFNOLRequestFormSubmit = (values: FNOL_RequestForm) => {
    setTimeZone(values.timeZone);
    const request = fnolRequestHelper(values);

    setQualifiedResourcesFilters(request);
    setFormSection(TaskCreate_FormSections.QUALIFIED_RESOURCES);
  };

  const ingressRequestHelper = (values: IngressRequestForm): IngressRequest => {
    const getClaimAttributesForIngress = () => {
      const attributesLookup: Record<string, string> =
        getClaimAttributeDisplayNameLookup(
          values.claimAttributes,
          company.attributeDefinition
        );

      return values.claimAttributes.reduce((acc, attribute) => {
        acc[String(attribute.displayName)] =
          attribute.selectedAttributeValuesVarchars
            .map((value) => attributesLookup[String(value)])
            .filter(Boolean);
        return acc;
      }, {} as Record<string, string[]>);
    };

    const startTime = zonedTimeToUtc(
      new Date(values.assignmentHandling.mustStartAfter),
      timeZone || ''
    ).toISOString();
    const endTime = zonedTimeToUtc(
      new Date(values.assignmentHandling.mustEndBefore),
      timeZone || ''
    ).toISOString();
    // A looked-up date is already an instant; a typed one is wall-clock time
    // at the loss location.
    const dateOfLoss = isDolLocked
      ? values.claim.dateOfLoss
      : zonedTimeToUtc(
          new Date(values.claim.dateOfLoss),
          timeZone || ''
        ).toISOString();

    const primaryContact = { ...values.contact.primary };
    if (!primaryContact.email) {
      delete primaryContact.email;
    }

    return {
      $msg: {
        externalId: uuidv4(),
        claim: {
          lossLocation: values.claim.lossLocation,
          jobId: values.claim.jobId,
          taskId: values.claim.taskId,
          claimNumber: values.claim.claimNumber,
          insuredName: values.claim.insuredName,
          catCode: values.claim.catCode,
          propertyType: values.claim.propertyType,
          dateOfLoss,
          dateOfNotice: values?.claim?.dateOfNotice,
          peril: values.claim.peril,
          sourcedFrom: values.claim.sourcedFrom,
          isModularHome: values.claim.isModularHome,
          jobNumber: values.claim.jobNumber,
        },
        contact: {
          primary: primaryContact,
          secondary: values.contact.hasSecondaryContact
            ? values.contact.secondary
            : undefined,
          altLocation: values.contact.hasAlternateLocation
            ? values.contact.altLocation
            : undefined,
        },
        assignmentHandling: {
          travel: values.assignmentHandling.travel,
          templateId: values.assignmentHandling.templateId,
          durationInMinutes: isNull(values.assignmentHandling.durationInMinutes)
            ? 0
            : values.assignmentHandling.durationInMinutes,
          priority: values.assignmentHandling.priority,
          severity: values.assignmentHandling.severity,
          methodOfInspection: FIELD,
          mustEndBefore: endTime,
          mustStartAfter: startTime,
          notes: values.assignmentHandling.notes,
          preferredLanguage: values.assignmentHandling.preferredLanguage,
          assignmentCategory: values.assignmentHandling.assignmentCategory,
          assignmentType: values.assignmentHandling.assignmentType,
          inspectionType: values.assignmentHandling.inspectionType,
        },
        requiredResource: selectedResource
          ? {
              name: selectedResource.name || '',
              id: selectedResource.id.toString() || '',
            }
          : undefined,
        taskType: FNOL_TaskTypes.CLAIM_OWNER,
      },
      requiredAttributes: getClaimAttributesForIngress(),
    };
  };

  const onIngressRequest = (response: { success: boolean }) => {
    if (response.success) {
      notifySuccess('Success', 'Task Ingressed successfully');
      navigate('/tasks');
      queryClient.invalidateQueries('infos');
    } else {
      notifyError('Error', 'Task Ingressed failed');
    }
  };

  const { ingressRequest, status: ingressStatus } =
    useIngressRequest(onIngressRequest);

  const onHandleIngressRequestFormSubmit = (values: IngressRequestForm) => {
    if (
      formSection === TaskCreate_FormSections.INGRESS ||
      formSection === TaskCreate_FormSections.INITIAL
    ) {
      setSaveButtonLabel('Ingress');
      setFormSection(TaskCreate_FormSections.REVIEW);

      return;
    }

    const request = applyAdditionalDataToIngressRequest(
      ingressRequestHelper(values),
      values.additionalData
    );

    ingressRequest({ request });
  };

  const { company } = useCompany();
  const { optionsWithIds: companyAttributes } = useGetCompanyAttributeOptions(
    company?.attributeDefinition || []
  );

  return (
    <QualifiedResourcesTaskCreate
      cwsStatus={cwsSummary.status}
      formSection={formSection}
      setFormSection={setFormSection}
      qualifiedResourcesPaging={qualifiedResourcesPost}
      qualifiedResourcesFilters={qualifiedResourcesFilters}
      setQualifiedResourcesFilters={setQualifiedResourcesFilters}
      fnolStatus={qualifiedResourcesPost.status}
      refetchQualified={qualifiedResourcesPost.refetch}
      selectedResource={selectedResource}
      setSelectedResource={setSelectedResource}
      setClaimNumberInput={setClaimNumberInput}
      qualifiedResources={qualifiedResources}
      cwsSummary={cwsSummary.summary}
      isDolLocked={isDolLocked}
      companyAttributes={companyAttributes}
      onHandleFNOLRequestFormSubmit={onHandleFNOLRequestFormSubmit}
      onHandleIngressRequestFormSubmit={onHandleIngressRequestFormSubmit}
      ingressStatus={ingressStatus}
      saveButtonLabel={saveButtonLabel}
      setSaveButtonLabel={setSaveButtonLabel}
      timeZone={timeZone}
    />
  );
}
